import React, { useContext, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { classNames } from "../../helpers/classNames";
import Button from "../../components/form/Button";
import { lngDetect } from "../../helpers/lngDetect";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { chatCreate } from "../../services/chatService";
import { toast } from "react-toastify";
import SocketContext, { useSocket } from "../../context/SocketContext";
import { t } from "i18next";

const menuItems = (event, lang) => {
  return [
    {
      label: "Orders",
      subItems: [
        {
          label: "All Orders",
          to: `/${lang}/user/myEvent/order/${event?.uid}/all`,
        },
        {
          label: "Pending Orders",
          to: `/${lang}/user/myEvent/order/${event?.uid}/pending`,
        },
        {
          label: "Cancelled Orders",
          to: `/${lang}/user/myEvent/order/${event?.uid}/cancelled`,
        },
      ],
    },
    {
      label: "Payments & Tax",
      subItems: [
        {
          label: "Transaction History",
          to: `/${lang}/user/myEvent/transactions/${event?.uid}`,
        },
        // {
        //   label: "Tax Settings",
        //   to: `/${lang}/user/myEvent/taxSetting/${event?.uid}`,
        // },
      ],
    },
    // {
    //   label: "Marketing",
    //   subItems: [
    //     { label: "Campaigns", to: `/${lang}/user/myEvent/${event?.uid}` },
    //     { label: "Discount Codes", to: `/${lang}/user/myEvent/${event?.uid}` },
    //   ],
    // },
    {
      label: "Manage Attendees",
      subItems: [
        {
          label: "Attendee List",
          to: `/${lang}/user/myEvent/attendees/${event?.uid}`,
        },
        // { label: "Invitations", to: `/${lang}/user/myEvent/${event?.uid}` },
        {
          label: "Add Attendee",
          to: `/${lang}/user/myEvent/${event?.uid}/attendees-add`,
        },
      ],
    },
    {
      label: "Reporting",
      subItems: [
        {
          label: "Event Reports",
          to: `/${lang}/user/myEvent/report/${event?.uid}`,
        },
        {
          label: "Customer Report",
          to: `/${lang}/user/customer_reports/${event?.uid}`,
        },
        // {
        //   label: "Revenue Analytics",
        //   to: `/${lang}/user/myEvent/${event?.uid}`,
        // },
      ],
    },
    // {
    //   label: "Promoters",
    //   subItems: [
    //     { label: "Lists", to: `/${lang}/user/promoters/${event?.uid}` },
    //   ],
    // },
  ];
};

const InnerPanel = ({ eventDetails = {}, selectedLanguage }) => {
  const navigate = useNavigate();
  const [message, setMessages] = useState({ count: 0 });
  const { session } = useSelector((state) => state?.authProvider);
  const [openMenus, setOpenMenus] = useState({});
  const { lang } = useSelector((state) => state?.langProvider);
  const { socket } = useContext(SocketContext);

  const steps = [
    {
      label: t("Build event page"),
      link: `/${lang}/user/events/event-manage/${eventDetails?.uid}`,
    },
    // "Online event page",
    // "Add tickets",
    // "Publish",
  ];

  const eventStatus = {
    onsale: { label: "On Sale" },
  };

  // const toggleMenu = (label) => {
  //   setOpenMenus((prev) => ({
  //     ...prev,
  //     [label]: !prev[label],
  //   }));
  // };

  // const toggleMenu = (label) => {
  //   setOpenMenus((prev) => {

  //     if (prev[label]) {
  //       return {};
  //     }

  //     return { [label]: true };
  //   });
  // };

  const toggleMenu = (label, hasSubItems) => {
    if (!hasSubItems) {
      setOpenMenus({}); // CLOSE all previously open dropdowns
    } else {
      setOpenMenus((prev) => (prev[label] ? {} : { [label]: true }));
    }
  };

  const handleMenuClick = (label, hasSubItems) => {
    if (!hasSubItems) {
      setOpenMenus({}); // close all dropdowns
    } else {
      setOpenMenus((prev) => (prev[label] ? {} : { [label]: true }));
    }
  };

  const currentStep = 2;

  useEffect(() => {
    if (eventDetails?._id) {
      socket?.emit("messageUnreadCountEvent", {
        user: session?.user?._id,
        countFor: "event",
        referenceId: eventDetails?._id,
      });
    }
  }, [eventDetails?._id]);

  useSocket(
    "messageUnreadCountEventResponse",
    (res) => {
      if (session?.user?._id === res?.user && res?.countFor === "event") {
        setMessages((pre) => ({ ...pre, count: res?.data }));
      }
    },
    [session?.user?._id]
  );

  useSocket(
    "getMessageDetailEventResponse",
    (res) => {
      socket?.emit("messageUnreadCountEvent", {
        user: session?.user?._id,
        countFor: "event",
        referenceId: eventDetails?._id,
      });
    },
    [session?.user?._id, eventDetails]
  );

  return (
    <div className="w-full lg:w-64 xl:w-80 bg-white rounded-md overflow-auto h-full py-5 px-5  scroll-smooth scrollbar scrollbar-default">
      {/* Back Link */}
      <div className="relative mb-3 flex">
        <Button
          buttonLabel={t("Back to events")}
          buttonLabelClasses={"text-deep-purple capitalize"}
          buttonIcon={"fa-regular fa-arrow-left"}
          buttonClasses={"bg-transparent px-0 h-auto text-deep-purple"}
          buttonHasLink
          buttonLink={`/${selectedLanguage}/user/events`}
        />
      </div>
      {/* Event Card */}
      <div className="group relative shadow rounded-lg cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 border border-transparent hover:border-primary/10 hover:no-underline">
        {/* <div className="bg-secondary-gradient h-2 w-full"></div> */}
        <div className="p-4">
          <h2 className="font-semibold text-lg text-deep-purple mb-1 font-Montserrat">
            {lngDetect(eventDetails?.title)}
          </h2>
          <p className="text-sm text-deep-purple mb-3 flex gap-1 items-center">
            <i className="fa-regular fa-calendar-days"></i>
            {eventDetails?.publishedAt
              ? dayjs(eventDetails?.publishedAt).format(
                  "ddd, DD MMM YYYY, hh:mm A"
                )
              : ""}
            {/* Fri, 11 July 2025, 10:00 am */}
          </p>
          <div className="flex items-center justify-between">
            {/* <span className="bg-primary/10 text-primary text-xs px-3 py-2 rounded-full flex items-center gap-2 font-semibold">
              <i className="fa-solid fa-circle"></i>{" "}
              {eventStatus[eventDetails?.eventStatus]?.label}
            </span> */}
            <Link
              target="_blank"
              to={`/${lang}/event-details/${eventDetails?.uid}`}
              className="text-deep-purple text-xs hover:underline ml-auto"
            >
              <div className="flex gap-2 items-center">
                {t("View")}
                <i className="fa-solid fa-up-right-from-square"></i>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="my-6">
        <h3 className="text-xs font-semibold text-deep-purple uppercase mb-2 tracking-wide">
          {t("Steps")}
        </h3>
        <ul className="space-y-2">
          {steps.map((step, idx) => {
            const isActive = idx === currentStep;

            return (
              <li key={idx}>
                <Link
                  to={step?.link}
                  className={classNames(
                    "flex items-center gap-3 text-sm px-3 py-2 rounded-md shadow-sm transition font-medium",
                    "hover:bg-primary/10",
                    isActive ? "bg-primary text-white" : " text-deep-purple"
                  )}
                >
                  <span
                    className={classNames(
                      "flex items-center justify-center w-5 h-5 rounded-full border-2 text-xs shrink-0",
                      isActive
                        ? "bg-deep-purple border-deep-purple text-white"
                        : "border-deep-purple text-deep-purple"
                    )}
                  >
                    ✓
                  </span>
                  <span>{step?.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <nav className="grid lg:block">
        <ul className="text-sm text-deep-purple font-medium gap-3 flex flex-row lg:flex-col overflow-auto w-full">
          {/* Dropdown Menus */}
          <li className="mb-2 ">
            <NavLink
              to={`/${lang}/user/myEvent/${eventDetails?.uid}`}
              className={({ isActive }) => {
                return classNames(
                  "block py-2 px-3 text-deep-purple hover:bg-primary/10  transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                  isActive
                    ? "child-link-active active bg-primary/10 text-deep-purple"
                    : ""
                );
              }}
              onClick={() => setOpenMenus({})}
            >
              {t("Dashboard")}
            </NavLink>
          </li>
          <li className="mb-2 ">
            <NavLink
              to={`/${lang}/user/check-out-form/${eventDetails?.uid}`}
              className={({ isActive }) => {
                return classNames(
                  "block py-2 px-3 text-deep-purple hover:bg-primary/10  transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                  isActive
                    ? "child-link-active active bg-primary/10 text-deep-purple"
                    : ""
                );
              }}
            >
              {t("Checkout form")}
            </NavLink>
          </li>
          <li className="mb-2">
            <NavLink
              to={`/${lang}/user/check-in/${eventDetails?.uid}`}
              className={({ isActive }) => {
                return classNames(
                  "block py-2 px-3 text-deep-purple hover:bg-primary/10 transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                  isActive
                    ? "child-link-active active bg-primary/10 text-deep-purple"
                    : ""
                );
              }}
              onClick={() => setOpenMenus({})}
            >
              {t("Check-in")}
            </NavLink>
          </li>
          {menuItems(eventDetails, selectedLanguage)?.map(
            ({ label, subItems }) => (
              <li key={label} className="mb-2">
                {/* Toggle Button */}
                <button
                  // onClick={() => toggleMenu(label)}
                  onClick={() => toggleMenu(label, (subItems || []).length > 0)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-primary/10 transition-all shadow-md lg:shadow-none rounded-full whitespace-nowrap"
                >
                  <span className="flex items-center gap-2">{t(label)}</span>
                  <span className="text-sm text-deep-purple">
                    <i
                      className={`fa-solid ${
                        openMenus[label] ? "fa-caret-down" : "fa-caret-right"
                      }`}
                    ></i>
                  </span>
                </button>

                {/* Dropdown Links */}
                {openMenus[label] && (
                  <div
                    className={`transition-all overflow-hidden duration-300 ease-in-out hidden lg:block ${
                      openMenus[label]
                        ? "max-h-40 opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <ul className="ml-5 mt-1 space-y-1 text-sm">
                      {(subItems || []).map((sub, i) => (
                        <li key={i}>
                          <NavLink
                            to={sub?.to}
                            className={({ isActive }) => {
                              return classNames(
                                "block py-2 px-3 text-deep-purple hover:bg-primary/10 rounded transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                                isActive
                                  ? "child-link-active active bg-primary/10 text-deep-purple"
                                  : ""
                              );
                            }}
                          >
                            {t(sub.label)}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* {openMenus[label] && (
                  <div className="transition-all duration-300 ease-in-out block lg:hidden">
                    <ul className="mt-1 text-sm flex flex-row lg:flex-col gap-2 ml-0 overflow-x-auto overflow-y-hidden max-w-full whitespace-nowrap">
                      {(subItems || []).map((sub, i) => (
                        <li key={i} className="flex-shrink-0">
                          <NavLink
                            to={sub?.to}
                            className={({ isActive }) =>
                              classNames(
                                "block py-2 px-3 text-deep-purple hover:bg-primary/10 rounded transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                                isActive
                                  ? "child-link-active active bg-primary/10 text-deep-purple"
                                  : ""
                              )
                            }
                          >
                            {sub.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                )} */}
              </li>
            )
          )}
          <li className="mb-2">
            <NavLink
              to={`/${lang}/user/promoters/${eventDetails?.uid}`}
              className={({ isActive }) => {
                return classNames(
                  "block py-2 px-3 text-deep-purple hover:bg-primary/10  transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                  isActive
                    ? "child-link-active active bg-primary/10 text-deep-purple"
                    : ""
                );
              }}
              onClick={() => setOpenMenus({})}
            >
              {t("Promoters")}
            </NavLink>
          </li>
          <li className="mb-2">
            <NavLink
              to={`/${lang}/user/communication/${eventDetails?.uid}`}
              className={({ isActive }) => {
                return classNames(
                  "flex items-center justify-between py-2 px-3 text-deep-purple hover:bg-primary/10 transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                  isActive
                    ? "child-link-active active bg-primary/10 text-deep-purple"
                    : ""
                );
              }}
              onClick={() => setOpenMenus({})}
            >
              {t("Communication")}
              {message?.count > 0 && (
                <div className="text-xs bg-primary p-1 rounded-full aspect-square w-7 flex items-center justify-center text-white">
                  {message?.count > 99 ? "99+" : message?.count}
                </div>
              )}
            </NavLink>
          </li>
        </ul>
      </nav>

      <div className="flex relative overflow-auto">
        {menuItems(eventDetails, selectedLanguage)?.map(
          ({ label, subItems }) => (
            <li key={label} className="mb-2 list-none">
              {openMenus[label] && (
                <div
                  className={`transition-all overflow-hidden duration-300 ease-in-out block lg:hidden ${
                    openMenus[label]
                      ? "max-h-40 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <ul className="mt-1 text-sm pb-2  flex flex-row lg:flex-col gap-2 ml-0 overflow-auto max-w-full">
                    {(subItems || []).map((sub, i) => (
                      <li key={i}>
                        <NavLink
                          to={sub?.to}
                          className={({ isActive }) => {
                            return classNames(
                              "block py-2 px-3 text-deep-purple hover:bg-primary/10 transition cursor-pointer shadow-md lg:shadow-none rounded-full whitespace-nowrap",
                              isActive
                                ? "child-link-active active bg-primary/10 text-deep-purple"
                                : ""
                            );
                          }}
                        >
                          {sub.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          )
        )}
      </div>
    </div>
  );
};

export default InnerPanel;
